import _ from 'underscore';
import { BaseView } from '@bingads-webui/base-view';
import { Selector } from '@bingads-webui/selector';
import { focusLockInstanceDecorator } from '@bingads-webui/focus-lock-decorator';

import domainSelectorTemplate from './domain-selector-template.pug';
import domainSelectorHeaderTemplate from './domain-selector-header-template.pug';

const SELECTOR_CHECK_STATES = {
  checked: 'checked',
  unchecked: 'unchecked',
};

export class DomainSelectorView extends BaseView {
  /**
   * Initializes a new instance of DomainSelectorView.
   * @param {Object} options.i18n - Localization component.
   * @param {boolean} [options.allowCheckAll=true] - Set to true to enable select all checkbox.
   * @param {int} [options.itemsDisplayLimit=50] - Max number of items to display in the selector.
   * @param {int} [options.itemsCheckedLimit=4] - Max number of items that can be checked. For
   * unlimited, set to zero.
   * @returns {void}
   */
  initialize({
    i18n,
    allowCheckAll = true,
    itemsDisplayLimit = 50,
    itemsCheckedLimit = 4,
  }) {
    this.i18n = i18n;
    this.domains = [];
    this.previouslyCheckedIds = [];

    this.allowCheckAll = allowCheckAll;
    this.itemsDisplayLimit = itemsDisplayLimit;
    this.itemsCheckedLimit = itemsCheckedLimit;
  }

  updateData({ items }) {
    this.domains = _.chain(items)
      .pluck('DisplayDomain')
      .without('You')
      .value();
    this.previouslyCheckedIds = [];

    this.renderSelector();
  }

  updateButton(domains) {
    let text = this.i18n.getString('AuctionInsights_Domain_Selector_Empty');

    if (!_.isEmpty(domains)) {
      text = this.i18n.getString('AuctionInsights_Domain_Selector_Template', { count: _.size(domains) });
    }

    this.$('.button-label').text(text);
  }

  render() {
    this.$el.html(domainSelectorTemplate({ i18n: this.i18n }));
    this.$('.dropdown').on('show.bs.dropdown', () => this.renderSelector());

    // subscribe to show/hide events (necessary to enable text input in the overview card)
    this.$('.dropdown').on('show.bs.dropdown', () => { this.trigger('dropdownVisible', true); });
    this.$('.dropdown').on('hide.bs.dropdown', () => { this.trigger('dropdownVisible', false); });

    this.renderSelector();
    this.updateButton();

    // bind to selection changes
    this.on('selectionChanged', (domains) => {
      this.updateButton(domains);
    });

    return this;
  }

  renderSelector() {
    this.removeSelector();

    this.$('.dropdown button').prop('disabled', _.isEmpty(this.domains));

    if (_.isEmpty(this.domains)) {
      return;
    }

    this.selector = new Selector({
      i18n: this.i18n,
      styles: {
        searchIcon: ['glyphicon', 'filter_search_icon_small'],
        list: ['table--fixed-layout', 'table--no-border'],
        nameColumn: ['table__cell--menu-item-style', 'table__cell--ellipsis-overflow'],
        searchBoxAriaLabel: this.i18n.getString('Search by competitor name'),
        listAttributes: { role: 'presentation' },
        checkboxColumn: ['table__cell--menu-item-style', 'table__cell--icon', 'legacy-competitor-selector-checkbox', 'clickable'],
        checkboxAttributes: { role: 'checkbox', tabindex: 0 },
      },
      busyIndicator: {
        enabled: true,
      },
      filter: {
        enabled: false,
        showSelected: false,
        showChanged: false,
      },
      showNoDataMessage: true,
      allowCheckAll: this.allowCheckAll,
      data: {
        itemsDisplayLimit: this.itemsDisplayLimit,
      },
    });

    this.selector.on('request:data', ({ searchFilter }) => {
      const data = _.chain(this.domains)
        .map((Name, Id) => {
          if (searchFilter && Name.indexOf(searchFilter) === -1) {
            return undefined;
          }
          return {
            Id,
            Name,
          };
        })
        .compact()
        .value();

      this.selector.setData(data);
    });

    this.selector.on('change:selection', ({ checked }) => {
      let hasSelectionChanged = true;
      let checkedIds = _.pluck(checked, 'id');

      if (this.itemsCheckedLimit > 0) {
        hasSelectionChanged = this.uncheckDomainsIfCheckedLimitExceeded(checkedIds);
        checkedIds = this.previouslyCheckedIds;
      }

      if (hasSelectionChanged) {
        const domainNames = _.map(checkedIds, id => this.domains[id]);
        this.trigger('selectionChanged', domainNames);
      }
    });

    this.FocusLockSelector = focusLockInstanceDecorator(this.selector.render(), {
      focusFirstItemOnMount: true,
    }, {
      managed: true,
    });
    this.focusLockSelectorView = new this.FocusLockSelector();

    this.$('.dropdown-menu').html(this.focusLockSelectorView.render().el);

    // render custom title
    this.$('.selector__main')
      .prepend(domainSelectorHeaderTemplate({ i18n: this.i18n }));
  }

  /**
   * Remembers the previous list of checked items and checks it against the current list.
   * If the number of checked items exceeds itemsCheckedLimit, uncheck the most recently checked
   * items so that limit is met.
   * @param {int[]} checkedIds - Array of ids that have been checked.
   * @returns {boolean} True if selection has changed from previous state.
   */
  uncheckDomainsIfCheckedLimitExceeded(checkedIds) {
    const numItemsToUncheck = _.size(checkedIds) - this.itemsCheckedLimit;
    if (numItemsToUncheck > 0) {
      // calculate the newly checked ids
      const newlyCheckedIds = _.difference(checkedIds, this.previouslyCheckedIds);

      // from newlyCheckedIds, pick the last items to uncheck
      let itemsToUncheck;
      let itemsToKeepChecked;
      if (_.size(newlyCheckedIds) > numItemsToUncheck) {
        // uncheck only a subset of newlyChecked
        itemsToKeepChecked = _.first(newlyCheckedIds, _.size(newlyCheckedIds) - numItemsToUncheck);
        itemsToUncheck = _.last(newlyCheckedIds, numItemsToUncheck);
      } else {
        // uncheck everything in newlyChecked
        itemsToUncheck = newlyCheckedIds;
      }

      if (itemsToKeepChecked) {
        // if unchecking only a subset of newlyCheckedId, track the new domains that remain checked
        this.previouslyCheckedIds.push(...itemsToKeepChecked);
      }

      // update the selector component with a new checkmap
      const { checkMap } = this.selector;
      _.each(itemsToUncheck, (id) => {
        checkMap[id].state = SELECTOR_CHECK_STATES.unchecked;
      });
      this.selector.setCheckMap(checkMap);

      // true if the selection changed from previous state, false if selection is the same
      return _.isArray(itemsToKeepChecked);
    }

    // nothing to uncheck
    this.previouslyCheckedIds = checkedIds;
    return true;
  }

  remove() {
    this.removeSelector();

    return super.remove();
  }

  removeSelector() {
    if (this.selector) {
      this.selector.remove();
      this.selector = null;
    }
  }
}
